import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CircleMarker,
  MapContainer,
  Polyline,
  Popup,
  TileLayer,
  useMap,
} from "react-leaflet";
import {
  Clock3,
  Gauge,
  LocateFixed,
  Navigation,
  RefreshCw,
  Route,
  Satellite,
} from "lucide-react";
import { Dialog } from "../../components/Dialog";
import { Button, EmptyState, Notice } from "../../components/ui";
import { api } from "../../lib/api";
import {
  createTripLocationSocket,
  parseTripLocationSocketMessage,
  type TripLocationAudience,
} from "../../lib/tripLocationSocket";
import type {
  AdminTripLocationEvent,
  AdminTripLocationHistory,
} from "../../types";

type TripLocationDialogProps = {
  tripId: string | null;
  onClose: () => void;
  audience?: TripLocationAudience;
};

type Coordinate = [number, number];
type LiveConnectionStatus =
  | "idle"
  | "connecting"
  | "live"
  | "reconnecting"
  | "fallback";

const SOCKET_HANDSHAKE_TIMEOUT_MS = 10_000;
const MAX_RECONNECT_DELAY_MS = 30_000;
const HTTP_FALLBACK_INITIAL_DELAY_MS = 30_000;
const HTTP_FALLBACK_MAX_DELAY_MS = 120_000;

function locationTimestamp(event: AdminTripLocationEvent) {
  return new Date(event.recordedAt).getTime();
}

function newestLocation(events: AdminTripLocationEvent[]) {
  return events.reduce<AdminTripLocationEvent | null>(
    (latest, event) =>
      !latest || locationTimestamp(event) > locationTimestamp(latest) ? event : latest,
    null,
  );
}

function mergeLocationEvents(
  current: AdminTripLocationEvent[],
  incoming: AdminTripLocationEvent[],
  limit: number,
) {
  const byId = new Map(current.map((event) => [event.id, event]));
  let addedCount = 0;

  for (const event of incoming) {
    if (!byId.has(event.id)) addedCount += 1;
    byId.set(event.id, event);
  }

  const allLocations = [...byId.values()].sort(
    (left, right) => locationTimestamp(right) - locationTimestamp(left),
  );

  return {
    locations: allLocations.slice(0, limit),
    addedCount,
    truncated: allLocations.length > limit,
    latestLocation: allLocations[0] ?? null,
  };
}

function mergeHistoryResponse(
  current: AdminTripLocationHistory,
  response: AdminTripLocationHistory,
) {
  const merged = mergeLocationEvents(
    current.locations,
    response.locations,
    response.pagination.limit,
  );
  const total = Math.max(
    current.pagination.total,
    response.pagination.total,
    merged.locations.length,
  );

  return {
    ...response,
    locations: merged.locations,
    latestLocation: merged.latestLocation,
    pagination: {
      ...response.pagination,
      total,
      hasMore: response.pagination.hasMore || merged.truncated || total > merged.locations.length,
    },
  };
}

function mergeLiveBatch(
  current: AdminTripLocationHistory,
  incoming: AdminTripLocationEvent[],
) {
  const merged = mergeLocationEvents(
    current.locations,
    incoming,
    current.pagination.limit,
  );
  const total = Math.max(
    current.pagination.total + merged.addedCount,
    merged.locations.length,
  );

  return {
    ...current,
    locations: merged.locations,
    latestLocation: merged.latestLocation,
    pagination: {
      ...current.pagination,
      total,
      hasMore: current.pagination.hasMore || merged.truncated || total > merged.locations.length,
    },
  };
}

const timeFormatter = new Intl.DateTimeFormat("tr-TR", {
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

function formatDateTime(value: string) {
  return timeFormatter.format(new Date(value));
}

function formatRelativeTime(value: string) {
  const elapsedSeconds = Math.round((new Date(value).getTime() - Date.now()) / 1000);
  const formatter = new Intl.RelativeTimeFormat("tr-TR", { numeric: "auto" });
  if (Math.abs(elapsedSeconds) < 60) return formatter.format(elapsedSeconds, "second");
  const elapsedMinutes = Math.round(elapsedSeconds / 60);
  if (Math.abs(elapsedMinutes) < 60) return formatter.format(elapsedMinutes, "minute");
  const elapsedHours = Math.round(elapsedMinutes / 60);
  if (Math.abs(elapsedHours) < 24) return formatter.format(elapsedHours, "hour");
  return formatter.format(Math.round(elapsedHours / 24), "day");
}

function formatSpeed(speed: number | null) {
  return speed === null ? "—" : `${Math.round(speed * 3.6)} km/sa`;
}

function tripStatusLabel(status: string) {
  return ({
    PLANNED: "Planlandı",
    ACTIVE: "Aktif",
    COMPLETED: "Tamamlandı",
    CANCELLED: "İptal Edildi",
  } as Record<string, string>)[status] ?? status;
}

const liveStatusPresentation: Record<
  LiveConnectionStatus,
  { label: string; detail: string; className: string }
> = {
  idle: {
    label: "Geçmiş kayıt",
    detail: "Canlı bağlantı kapalı",
    className: "bg-slate-100 text-slate-600",
  },
  connecting: {
    label: "Bağlanıyor",
    detail: "Canlı bağlantı kuruluyor",
    className: "bg-amber-50 text-amber-700",
  },
  live: {
    label: "Canlı",
    detail: "Yeni konumlar anında aktarılıyor",
    className: "bg-emerald-50 text-emerald-700",
  },
  reconnecting: {
    label: "Yeniden bağlanıyor",
    detail: "Canlı bağlantı tekrar kuruluyor",
    className: "bg-amber-50 text-amber-700",
  },
  fallback: {
    label: "HTTP yedek",
    detail: "Konumlar kontrollü aralıklarla yenileniyor",
    className: "bg-sky-50 text-sky-700",
  },
};

function FitLocationBounds({ points }: { points: Coordinate[] }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 15);
      return;
    }
    map.fitBounds(points, { padding: [36, 36], maxZoom: 16 });
  }, [map, points]);
  return null;
}

function FocusLocation({ point }: { point: Coordinate | null }) {
  const map = useMap();
  useEffect(() => {
    if (point) map.flyTo(point, Math.max(map.getZoom(), 16), { duration: 0.5 });
  }, [map, point]);
  return null;
}

function LocationRow({
  event,
  active,
  onSelect,
}: {
  event: AdminTripLocationEvent;
  active: boolean;
  onSelect: () => void;
}) {
  const uploadDelaySeconds = Math.max(
    0,
    Math.round(
      (new Date(event.receivedAt).getTime() - new Date(event.recordedAt).getTime()) /
        1000,
    ),
  );
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full rounded-2xl border p-4 text-left transition ${
        active
          ? "border-teal bg-teal/5 shadow-sm"
          : "border-slate-200 bg-white hover:border-teal/40 hover:bg-mist/60"
      }`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-bold text-navy">#{event.sequenceNumber}</p>
          <p className="mt-1 text-sm font-semibold text-slate-700">
            {formatDateTime(event.recordedAt)}
          </p>
          <p className="mt-1 text-xs text-slate-400">
            Cihaz kaydı · {formatRelativeTime(event.recordedAt)}
          </p>
        </div>
        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
          ±{Math.round(event.accuracy)} m
        </span>
      </div>
      <div className="mt-4 grid gap-2 text-xs text-slate-500 sm:grid-cols-2">
        <span className="flex items-center gap-2">
          <Gauge size={14} className="text-teal" /> {formatSpeed(event.speed)}
        </span>
        <span className="flex items-center gap-2">
          <Navigation size={14} className="text-coral" />
          {event.heading === null ? "Yön yok" : `${Math.round(event.heading)}°`}
        </span>
        <span className="flex items-center gap-2 sm:col-span-2">
          <Satellite size={14} className="text-slate-400" />
          Sunucuya ulaştı: {formatDateTime(event.receivedAt)}
          {uploadDelaySeconds > 5 ? ` · ${uploadDelaySeconds} sn gecikme` : ""}
        </span>
      </div>
      <p className="mt-3 font-mono text-[11px] text-slate-400">
        {event.latitude.toFixed(6)}, {event.longitude.toFixed(6)}
      </p>
    </button>
  );
}

export function TripLocationDialog({
  tripId,
  onClose,
  audience = "admin",
}: TripLocationDialogProps) {
  const [data, setData] = useState<AdminTripLocationHistory | null>(null);
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [focusedLocationId, setFocusedLocationId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [liveStatus, setLiveStatus] = useState<LiveConnectionStatus>("idle");
  const activeRequestKeyRef = useRef("");
  const loadInFlightRef = useRef<{
    key: string;
    promise: Promise<void>;
  } | null>(null);

  const load = useCallback(
    (background = false): Promise<void> => {
      if (!tripId) return Promise.resolve();

      const requestKey = `${audience}:${tripId}`;
      activeRequestKeyRef.current = requestKey;
      if (loadInFlightRef.current?.key === requestKey) {
        return loadInFlightRef.current.promise;
      }

      const scope = audience === "company" ? "organizations" : "admin";
      const request = (async () => {
        if (background) setRefreshing(true);
        else setLoading(true);
        try {
          const response = await api<AdminTripLocationHistory>(
            `/${scope}/trips/${encodeURIComponent(tripId)}/locations?limit=500`,
          );
          if (activeRequestKeyRef.current !== requestKey) return;

          setData((current) =>
            background && current
              ? mergeHistoryResponse(current, response)
              : response,
          );
          setError("");
          setSelectedLocationId((current) =>
            current && response.locations.some((item) => item.id === current)
              ? current
              : response.latestLocation?.id ?? response.locations[0]?.id ?? null,
          );
        } catch (loadError) {
          if (activeRequestKeyRef.current !== requestKey) return;
          setError(
            loadError instanceof Error
              ? loadError.message
              : "Konum geçmişi yüklenemedi.",
          );
        } finally {
          if (activeRequestKeyRef.current === requestKey) {
            setLoading(false);
            setRefreshing(false);
          }
        }
      })();

      loadInFlightRef.current = { key: requestKey, promise: request };
      void request.finally(() => {
        if (loadInFlightRef.current?.promise === request) {
          loadInFlightRef.current = null;
        }
      });
      return request;
    },
    [audience, tripId],
  );

  useEffect(() => {
    if (!tripId) {
      activeRequestKeyRef.current = "";
      setData(null);
      setSelectedLocationId(null);
      setFocusedLocationId(null);
      setError("");
      setLiveStatus("idle");
      return;
    }
    activeRequestKeyRef.current = `${audience}:${tripId}`;
    setData(null);
    setSelectedLocationId(null);
    setFocusedLocationId(null);
    setError("");
    setLiveStatus("idle");
    void load();
  }, [audience, load, tripId]);

  useEffect(() => {
    if (!tripId || data?.trip.id !== tripId || data.trip.status !== "ACTIVE") {
      setLiveStatus("idle");
      return;
    }

    let disposed = false;
    let socket: WebSocket | null = null;
    let reconnectTimer: number | null = null;
    let handshakeTimer: number | null = null;
    let retryAttempt = 0;

    const clearHandshakeTimer = () => {
      if (handshakeTimer !== null) window.clearTimeout(handshakeTimer);
      handshakeTimer = null;
    };

    const scheduleReconnect = () => {
      if (disposed || reconnectTimer !== null) return;
      setLiveStatus("fallback");

      const exponentialDelay = Math.min(
        MAX_RECONNECT_DELAY_MS,
        1_000 * 2 ** Math.min(retryAttempt, 5),
      );
      const jitteredDelay = Math.round(exponentialDelay * (0.75 + Math.random() * 0.5));
      retryAttempt += 1;
      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = null;
        connect(true);
      }, jitteredDelay);
    };

    const connect = (reconnecting: boolean) => {
      if (disposed) return;
      setLiveStatus(reconnecting ? "reconnecting" : "connecting");

      try {
        socket = createTripLocationSocket(tripId, audience);
      } catch {
        scheduleReconnect();
        return;
      }

      const activeSocket = socket;
      let ready = false;

      handshakeTimer = window.setTimeout(() => {
        if (!disposed && !ready) activeSocket.close(4000, "Canlı bağlantı zaman aşımı");
      }, SOCKET_HANDSHAKE_TIMEOUT_MS);

      activeSocket.onmessage = (event) => {
        if (disposed) return;
        const message = parseTripLocationSocketMessage(event.data);
        if (!message) return;
        if (message.tripId !== tripId) {
          activeSocket.close(1008, "Sefer eşleşmedi");
          return;
        }

        if (!ready) {
          ready = true;
          clearHandshakeTimer();
          retryAttempt = 0;
          setLiveStatus("live");
          if (reconnecting) void load(true);
        }

        if (message.type === "trip.location.batch") {
          const latestInBatch = newestLocation(message.locations);
          setData((current) =>
            current ? mergeLiveBatch(current, message.locations) : current,
          );
          setSelectedLocationId(latestInBatch?.id ?? null);
          setFocusedLocationId(latestInBatch?.id ?? null);
        }
      };

      activeSocket.onerror = () => {
        if (!disposed) activeSocket.close();
      };

      activeSocket.onclose = () => {
        clearHandshakeTimer();
        if (socket === activeSocket) socket = null;
        if (!disposed) scheduleReconnect();
      };
    };

    connect(false);

    return () => {
      disposed = true;
      clearHandshakeTimer();
      if (reconnectTimer !== null) window.clearTimeout(reconnectTimer);
      reconnectTimer = null;
      if (socket) {
        socket.onopen = null;
        socket.onmessage = null;
        socket.onerror = null;
        socket.onclose = null;
        socket.close(1000, "Ekran kapatıldı");
        socket = null;
      }
    };
  }, [audience, data?.trip.id, data?.trip.status, load, tripId]);

  const httpFallbackActive =
    data?.trip.status === "ACTIVE" &&
    (liveStatus === "fallback" || liveStatus === "reconnecting");

  useEffect(() => {
    if (!tripId || data?.trip.id !== tripId || !httpFallbackActive) return;

    let disposed = false;
    let pollAttempt = 0;
    let pollTimer: number | null = null;

    const schedulePoll = () => {
      const delay = Math.min(
        HTTP_FALLBACK_MAX_DELAY_MS,
        HTTP_FALLBACK_INITIAL_DELAY_MS * 2 ** Math.min(pollAttempt, 2),
      );
      pollTimer = window.setTimeout(async () => {
        pollTimer = null;
        if (document.visibilityState === "visible") {
          await load(true);
          pollAttempt += 1;
        }
        if (!disposed) schedulePoll();
      }, delay);
    };

    schedulePoll();
    return () => {
      disposed = true;
      if (pollTimer !== null) window.clearTimeout(pollTimer);
    };
  }, [data?.trip.id, httpFallbackActive, load, tripId]);

  const chronologicalLocations = useMemo(
    () =>
      [...(data?.locations ?? [])].sort(
        (left, right) =>
          new Date(left.recordedAt).getTime() - new Date(right.recordedAt).getTime(),
      ),
    [data?.locations],
  );
  const newestFirstLocations = useMemo(
    () => [...chronologicalLocations].reverse(),
    [chronologicalLocations],
  );
  const routePoints = useMemo<Coordinate[]>(
    () => chronologicalLocations.map((item) => [item.latitude, item.longitude]),
    [chronologicalLocations],
  );
  const selectedLocation =
    data?.locations.find((item) => item.id === selectedLocationId) ?? null;
  const selectedPoint: Coordinate | null = selectedLocation
    ? [selectedLocation.latitude, selectedLocation.longitude]
    : null;
  const latestPoint: Coordinate | null = data?.latestLocation
    ? [data.latestLocation.latitude, data.latestLocation.longitude]
    : null;
  const focusedLocation =
    data?.locations.find((item) => item.id === focusedLocationId) ?? null;
  const focusedPoint: Coordinate | null = focusedLocation
    ? [focusedLocation.latitude, focusedLocation.longitude]
    : null;
  const quote = data?.trip.assignment.quote;
  const mapBounds = useMemo<Coordinate[]>(() => {
    if (routePoints.length > 0) return routePoints;
    if (!quote) return [];
    return [
      [quote.originLat, quote.originLon],
      [quote.destinationLat, quote.destinationLon],
    ];
  }, [quote, routePoints]);
  const connectionPresentation = liveStatusPresentation[liveStatus];

  return (
    <Dialog
      open={Boolean(tripId)}
      onClose={onClose}
      title={quote ? `${quote.quoteNumber} — Konum Takibi` : "Sefer Konum Takibi"}
      description="Şoförün cihazından alınan son konumu, izlenen yolu ve kayıt zamanlarını inceleyin."
      size="xl"
    >
      {loading && !data ? (
        <div className="grid min-h-80 place-items-center text-sm font-semibold text-slate-500">
          <RefreshCw className="mb-3 animate-spin text-teal" /> Konum geçmişi yükleniyor…
        </div>
      ) : error && !data ? (
        <div className="space-y-4">
          <Notice tone="error">{error}</Notice>
          <Button type="button" variant="secondary" onClick={() => void load()}>
            Tekrar Dene
          </Button>
        </div>
      ) : data ? (
        <div className="space-y-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="font-bold text-navy">{data.trip.driver.fullName}</p>
              <p className="mt-1 text-sm text-slate-500">
                {quote?.organization.name}
                {data.trip.vehicle
                  ? ` · ${data.trip.vehicle.plateNumber} · ${data.trip.vehicle.brand} ${data.trip.vehicle.model}`
                  : " · Sefer aracı seçilmedi"}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="text-right">
                <span
                  className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold ${connectionPresentation.className}`}
                >
                  <span
                    className={`h-2 w-2 rounded-full bg-current ${liveStatus === "live" ? "animate-pulse" : ""}`}
                  />
                  {connectionPresentation.label}
                </span>
                <p className="mt-1 flex items-center justify-end gap-1 text-[11px] text-slate-400">
                  <Clock3 size={13} /> {connectionPresentation.detail}
                </p>
              </div>
              <Button
                type="button"
                variant="ghost"
                loading={refreshing}
                onClick={() => void load(true)}
              >
                <RefreshCw size={16} /> Yenile
              </Button>
            </div>
          </div>

          {error && <Notice tone="warning">Son yenileme başarısız: {error}</Notice>}

          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-2xl bg-mist p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Son Konum</p>
              <p className="mt-2 font-bold text-navy">
                {data.latestLocation
                  ? formatRelativeTime(data.latestLocation.recordedAt)
                  : "Henüz alınmadı"}
              </p>
              {data.latestLocation && (
                <p className="mt-1 text-xs text-slate-500">
                  {formatDateTime(data.latestLocation.recordedAt)}
                </p>
              )}
            </div>
            <div className="rounded-2xl bg-mist p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Gösterilen Kayıt</p>
              <p className="mt-2 font-bold text-navy">{data.locations.length} konum noktası</p>
              <p className="mt-1 text-xs text-slate-500">
                {data.pagination.hasMore ? "Daha eski kayıtlar mevcut" : "Tüm kayıtlar gösteriliyor"}
              </p>
            </div>
            <div className="rounded-2xl bg-mist p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Sefer Durumu</p>
              <p className="mt-2 font-bold text-navy">{tripStatusLabel(data.trip.status)}</p>
              <p className="mt-1 text-xs text-slate-500">
                {data.trip.startedAt
                  ? `Başlangıç: ${formatDateTime(data.trip.startedAt)}`
                  : "Takip henüz başlatılmadı"}
              </p>
            </div>
          </div>

          <div className="relative h-[420px] overflow-hidden rounded-3xl border border-slate-200 bg-[#dce9e5]">
            <MapContainer
              center={[39.1, 35.2]}
              zoom={6}
              scrollWheelZoom
              className="absolute inset-0"
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> katkıcıları'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {quote && (
                <>
                  <CircleMarker
                    center={[quote.originLat, quote.originLon]}
                    radius={8}
                    pathOptions={{ color: "#fff", weight: 3, fillColor: "#14a89d", fillOpacity: 1 }}
                  >
                    <Popup>Çıkış: {quote.originLabel}</Popup>
                  </CircleMarker>
                  <CircleMarker
                    center={[quote.destinationLat, quote.destinationLon]}
                    radius={8}
                    pathOptions={{ color: "#fff", weight: 3, fillColor: "#ff6b4a", fillOpacity: 1 }}
                  >
                    <Popup>Varış: {quote.destinationLabel}</Popup>
                  </CircleMarker>
                </>
              )}
              {routePoints.length > 1 && (
                <Polyline
                  positions={routePoints}
                  pathOptions={{ color: "#10233f", weight: 5, opacity: 0.82 }}
                />
              )}
              {data.latestLocation &&
                latestPoint &&
                data.latestLocation.id !== selectedLocation?.id && (
                  <CircleMarker
                    center={latestPoint}
                    radius={10}
                    pathOptions={{
                      color: "#fff",
                      weight: 4,
                      fillColor: "#ff6b4a",
                      fillOpacity: 1,
                    }}
                  >
                    <Popup>
                      <strong>Son konum #{data.latestLocation.sequenceNumber}</strong>
                      <br />
                      {formatDateTime(data.latestLocation.recordedAt)}
                    </Popup>
                  </CircleMarker>
                )}
              {selectedLocation && selectedPoint && (
                <CircleMarker
                  center={selectedPoint}
                  radius={10}
                  pathOptions={{ color: "#fff", weight: 4, fillColor: "#14a89d", fillOpacity: 1 }}
                >
                  <Popup>
                    <strong>Konum #{selectedLocation.sequenceNumber}</strong>
                    <br />
                    {formatDateTime(selectedLocation.recordedAt)}
                  </Popup>
                </CircleMarker>
              )}
              <FitLocationBounds points={mapBounds} />
              <FocusLocation point={focusedPoint} />
            </MapContainer>
            {data.locations.length === 0 && (
              <div className="pointer-events-none absolute left-4 top-4 z-[500] rounded-2xl bg-white/95 p-4 shadow-lg backdrop-blur">
                <p className="flex items-center gap-2 font-semibold text-navy">
                  <LocateFixed size={17} className="text-coral" /> Konum bekleniyor
                </p>
                <p className="mt-1 text-xs text-slate-500">Şoför takibi başlattığında rota burada oluşur.</p>
              </div>
            )}
          </div>

          <section>
            <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
              <div>
                <h3 className="flex items-center gap-2 text-lg font-bold text-navy">
                  <Route size={19} className="text-teal" /> Konum Zaman Çizelgesi
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  Bir kayda tıklayarak haritadaki konumuna odaklanabilirsiniz.
                </p>
              </div>
            </div>
            {newestFirstLocations.length === 0 ? (
              <EmptyState
                title="Henüz konum kaydı yok"
                text="Mobil uygulamada sefer takibi başladıktan ve ilk kayıt sunucuya ulaştıktan sonra burada görünecek."
              />
            ) : (
              <div className="grid max-h-[520px] gap-3 overflow-y-auto pr-1 md:grid-cols-2">
                {newestFirstLocations.map((event) => (
                  <LocationRow
                    key={event.id}
                    event={event}
                    active={event.id === selectedLocationId}
                    onSelect={() => {
                      setSelectedLocationId(event.id);
                      setFocusedLocationId(event.id);
                    }}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      ) : null}
    </Dialog>
  );
}
